import React, { useState, useEffect } from 'react';
import Tooltip from '@mui/material/Tooltip';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

interface PaginationProps {
    data: {
        total_items: number;
        total_pages: number;
        current_page: number;
        data: any[];
    };
    handlePageChange: (pageNumber: number) => void;
}

const PaginationContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 8px;
`;

const PaginationItem = styled(({ active, ...props }: { active?: boolean, [key: string]: any }) => <div {...props} />)<{ active?: boolean }>`
    border-radius: 50%;
    width: 30px;
    height: 30px;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: ${props => (props.active ? '#CCCCCF' : '')};
    color: ${props => (props.active ? '#878B8E' : '#CCCCCF')};
    margin: 0 5px;
    cursor: pointer;

    &:hover {
        background-color: #CCCCCF;
        color: #878B8E;
    }
`;

const PaginationControl = styled.div`
    border-radius: 50%;
    width: 30px;
    height: 30px;
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0 5px;
    cursor: pointer;
    color: #CCCCCF;

    &:hover {
        background-color: #CCCCCF;
        color: #878B8E;
    }
`;

const CustomPagination: React.FC<PaginationProps> = ({
    data,
    handlePageChange: handlePageChangeProp
}) => {
    const { total_pages, current_page } = data;
    const { t } = useTranslation('common');
    const [firstPage, setFirstPage] = useState(1);
    const pagesToShow = 5;

    useEffect(() => {
        if (current_page < firstPage || current_page >= firstPage + pagesToShow) {
            setFirstPage(current_page);
        }
    }, [current_page]);

    const handlePageChange = (pageNumber: number) => {
        handlePageChangeProp(pageNumber);
    };

    const nextPage = () => {
        handlePageChange(Math.min(total_pages, current_page + 1));
    };

    const prevPage = () => {
        handlePageChange(Math.max(1, current_page - 1));
    };

    // Acrescentando um prefixo à key para evitar duplicação mesmo se houver outra instância.
    const paginationItems = [];
    for (
        let number = firstPage;
        number < firstPage + pagesToShow && number <= total_pages;
        number++
    ) {
        paginationItems.push(
            <PaginationItem
                key={`custom-pagination-${number}`}
                active={number === current_page}
                onClick={() => handlePageChange(number)}
            >
                {number}
            </PaginationItem>
        );
    }

    return (
        <PaginationContainer>
            {current_page > 1 && (
                <>
					<Tooltip title={t('pagination.first')} enterTouchDelay={0} leaveTouchDelay={2000} arrow placement='top'>
						<PaginationControl key="first" onClick={() => handlePageChange(1)}>
							{'<<'}
						</PaginationControl>
					</Tooltip>
					<Tooltip title={t('pagination.previous')} enterTouchDelay={0} leaveTouchDelay={2000} arrow placement='top'>
						<PaginationControl key="prev" onClick={prevPage}>
							{'<'}
						</PaginationControl>
					</Tooltip>
                </>
            )}
            {paginationItems}
            {current_page < total_pages && (
                <>
					<Tooltip title={t('pagination.next')} enterTouchDelay={0} leaveTouchDelay={2000} arrow placement='top'>
						<PaginationControl key="next" onClick={nextPage}>
							{'>'}
						</PaginationControl>
					</Tooltip>
					<Tooltip title={t('pagination.last')} enterTouchDelay={0} leaveTouchDelay={2000} arrow placement='top'>
						<PaginationControl key="last" onClick={() => handlePageChange(total_pages)}>
							{'>>'}
						</PaginationControl>
					</Tooltip>
                </>
            )}
        </PaginationContainer>
    );
};

export default CustomPagination;
